class CvHeader extends HTMLElement {
	connectedCallback() {
		this.abort = new AbortController();
		this.triggerModeAbortController = new AbortController();
		this.desktopRelocateDropdowns();
		this.desktopSetupNav();
		this.drawerSetup();
		this.stickySetup();
	}

	disconnectedCallback() {
		this.abort.abort();
		this.triggerModeAbortController.abort();
		if (this.drawerIsOpen()) this.drawerClose();
	}

	// ── Desktop nav ──

	desktopRelocateDropdowns() {
		const section = this.querySelector(".cv-section");
		if (!section) return;
		this.querySelectorAll(".cv-header__dropdown").forEach((panel) => {
			section.appendChild(panel);
		});
	}

	desktopSetupNav() {
		this.desktopTriggers = this.querySelectorAll(
			".cv-header__nav-trigger[aria-controls]"
		);

		this.desktopTriggerMode = this.dataset.triggerMode || "hover";

		this.desktopTriggers.forEach((trigger) => {
			const item = trigger.closest(".cv-header__nav-section");
			const panel = this.desktopPanelFor(trigger);
			if (!item || !panel) return;

			trigger.addEventListener("keydown", (e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					this.desktopToggleDropdown(trigger, panel);
				}
				if (e.key === "Escape") {
					this.desktopCloseAllDropdowns();
					trigger.focus();
				}
			});
		});

		this.desktopSetTriggerMode(this.desktopTriggerMode);

		document.addEventListener(
			"click",
			(e) => {
				if (!this.contains(e.target)) this.desktopCloseAllDropdowns();
			},
			{ signal: this.abort.signal }
		);
	}

	desktopSetTriggerMode(mode) {
		this.triggerModeAbortController.abort();
		this.triggerModeAbortController = new AbortController();

		this.desktopTriggers.forEach((trigger) => {
			const item = trigger.closest(".cv-header__nav-section");
			const panel = this.desktopPanelFor(trigger);
			if (!item || !panel) return;

			if (mode === "click" || trigger.getAttribute("href") === "") {
				trigger.addEventListener(
					"click",
					(e) => {
						e.preventDefault();
						this.desktopToggleDropdown(trigger, panel);
					},
					{ signal: this.triggerModeAbortController.signal }
				);
			}

			if (mode === "hover") {
				let timeout;
				const open = () => {
					clearTimeout(timeout);
					this.desktopCloseAllDropdowns();
					this.desktopOpenDropdown(trigger, panel);
				};
				const startClose = () => {
					timeout = setTimeout(
						() => this.desktopCloseDropdown(trigger, panel),
						300
					);
				};

				item.addEventListener("mouseenter", open, {
					signal: this.triggerModeAbortController.signal,
				});
				item.addEventListener("mouseleave", startClose, {
					signal: this.triggerModeAbortController.signal,
				});
				panel.addEventListener("mouseenter", () => clearTimeout(timeout), {
					signal: this.triggerModeAbortController.signal,
				});
				panel.addEventListener("mouseleave", startClose, {
					signal: this.triggerModeAbortController.signal,
				});
			}
		});
	}

	desktopPanelFor(trigger) {
		const id = trigger.getAttribute("aria-controls");
		return id ? document.getElementById(id) : null;
	}

	desktopToggleDropdown(trigger, panel) {
		const isOpen = panel.getAttribute("aria-hidden") === "false";
		this.desktopCloseAllDropdowns();
		if (!isOpen) this.desktopOpenDropdown(trigger, panel);
	}

	desktopOpenDropdown(trigger, panel) {
		this.setDisclosureState(trigger, panel, true);
	}

	desktopCloseDropdown(trigger, panel) {
		this.setDisclosureState(trigger, panel, false);
	}

	desktopCloseAllDropdowns() {
		this.desktopTriggers.forEach((trigger) => {
			const panel = this.desktopPanelFor(trigger);
			if (panel) this.desktopCloseDropdown(trigger, panel);
		});
	}

	// ── Mobile drawer ──

	drawerSetup() {
		this.drawerEl = this.querySelector(".cv-header__drawer");
		this.drawerBackdrop = this.querySelector(".cv-header__drawer-backdrop");
		this.drawerHamburger = this.querySelector(".cv-header__hamburger");
		this.closeBtn = this.querySelector(".cv-header__drawer-close");
		this.returnToMainMenuBtn = this.querySelector(
			".cv-header__back-to-main-menu"
		);

		if (!this.drawerEl || !this.drawerHamburger) return;

		this.drawerPopulate();

		this.drawerHamburger.addEventListener("click", () => this.drawerOpen());

		if (this.closeBtn)
			this.closeBtn.addEventListener("click", () => this.drawerClose());

		if (this.returnToMainMenuBtn)
			this.returnToMainMenuBtn.addEventListener("click", () =>
				this.closeAllSubmenus()
			);

		if (this.drawerBackdrop)
			this.drawerBackdrop.addEventListener("click", () => this.drawerClose());

		document.addEventListener(
			"keydown",
			(e) => {
				if (e.key === "Escape" && this.drawerIsOpen()) this.drawerClose();
			},
			{ signal: this.abort.signal }
		);
	}

	drawerPopulate() {
		this.list = this.drawerEl.querySelector(".cv-header__drawer-sections");
		this.drawerContentHolder = this.drawerEl.querySelector(
			".cv-header__drawer-content"
		);
		if (!this.list || !this.drawerContentHolder) return;

		this.list.replaceChildren();

		this.querySelectorAll(".cv-header__nav-section").forEach((section) => {
			const trigger = section.querySelector(".cv-header__nav-trigger");
			if (!trigger) return;

			const title = trigger.textContent.trim();
			const panel = this.desktopPanelFor(trigger);
			const li = document.createElement("li");

			if (panel) {
				li.appendChild(this.drawerBuildDisclosure(title, panel));
			} else {
				const a = document.createElement("a");
				a.href = (trigger.closest("a") || trigger).href || "#";
				a.textContent = title;
				li.appendChild(a);
			}

			this.list.appendChild(li);
		});
	}

	drawerBuildDisclosure(title, panel) {
		const summary = document.createElement("div");
		summary.textContent = title;
		summary.ariaControls = `${title}-menu-items`;
		summary.className = "cv-header__drawer-section-summary";

		const arrow = document.createElement("div");
		arrow.innerHTML = document
			.querySelector(".cv-header__arrow-right")
			.cloneNode(true).innerHTML;
		summary.appendChild(arrow);

		const contentWrapper = document.createElement("div");
		contentWrapper.className = "cv-header__menu-section-wrapper";
		contentWrapper.id = `${title}-menu-items`;

		const inner = panel.querySelector(".cv-header__dropdown-inner");
		if (inner) {
			const clone = inner.cloneNode(true);
			const innerContent = document.createElement("div");
			innerContent.className = "cv-header__menu-section-inner";

			const stack = clone.querySelector(".cv-stack");
			if (stack) {
				const submenuItems = Array.from(stack.children).filter(
					(child) => !child.classList.contains("cv-header__nav-promo")
				);

				submenuItems.forEach((child, index) => {
					child.classList.add("cv-header__submenu-section");

					innerContent.appendChild(child);
					if (index !== submenuItems.length - 1) {
						const divider = document.createElement("div");
						divider.innerHTML = "&nbsp;";
						divider.className = "cv-header__divider";
						innerContent.append(divider);
					}
				});
			}

			if (innerContent.children.length)
				contentWrapper.appendChild(innerContent);
		}

		summary.addEventListener("click", () => {
			contentWrapper.classList.add("cv-header__menu-section-open");
			this.list.classList.add("cv-header__drawer-sections-hidden");
			this.closeBtn.style.display = "none";
			this.returnToMainMenuBtn.style.display = "flex";
		});

		this.drawerContentHolder.appendChild(contentWrapper);
		return summary;
	}

	closeAllSubmenus() {
		const openDrawers = document.querySelectorAll(
			".cv-header__menu-section-open"
		);

		openDrawers.forEach((openDrawer) => {
			openDrawer.classList.remove("cv-header__menu-section-open");
		});

		this.list.classList.remove("cv-header__drawer-sections-hidden");
		this.closeBtn.style.display = "flex";
		this.returnToMainMenuBtn.style.display = "none";
	}

	drawerIsOpen() {
		return this.drawerEl?.getAttribute("aria-hidden") === "false";
	}

	drawerOpen() {
		this.setDisclosureState(this.drawerHamburger, this.drawerEl, true);
	}

	drawerClose() {
		this.setDisclosureState(this.drawerHamburger, this.drawerEl, false);
		this.closeAllSubmenus();
	}

	// ── Sticky ──

	stickySetup() {
		const stickyType = this.dataset.stickyType;
		if (stickyType === "none") return;

		const stickyWrapper = this.closest(".cv-header-section");
		if (!stickyWrapper) return;

		stickyWrapper.classList.add("cv-header-section--sticky");
		if (stickyType === "always") return;

		let lastScrollTop = 0;
		stickyWrapper.style.setProperty(
			"--cv-header-height",
			`${this.offsetHeight}px`
		);
		window.addEventListener(
			"scroll",
			() => {
				const scrollTop = window.scrollY;
				if (scrollTop > lastScrollTop && scrollTop > this.offsetHeight) {
					stickyWrapper.classList.add("cv-header-section--hidden");
				} else {
					stickyWrapper.classList.remove("cv-header-section--hidden");
				}
				lastScrollTop = scrollTop;
			},
			{ passive: true, signal: this.abort.signal }
		);
	}

	// ── Helpers ──

	setDisclosureState(trigger, panel, isOpen) {
		trigger.setAttribute("aria-expanded", String(isOpen));
		panel.setAttribute("aria-hidden", String(!isOpen));
	}
}

if (!customElements.get("cv-header")) {
	customElements.define("cv-header", CvHeader);
}
